import { UseMutationResult } from "@tanstack/react-query";
import React, { useState } from "react";
import classNames from "classnames";
import { FinancialAccount } from "@common/types/financialAccount";
import { checkIfEnteredAccountNameIsValidForConfirmation } from "@common/helpers/financialAccount";
import { pluralize } from "@common/helpers/string";
import { useLockedTransactionsForFinancialAccount } from "@hooks/useLockedTransactionsForFinancialAccount";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import styles from "./RemoveFinancialAccount.module.scss";

interface RemoveAccountConfirmationContentProps {
    account: FinancialAccount;
    back?: () => void;
    removeAccountMutation: UseMutationResult<
        unknown,
        unknown,
        { forceDeletion?: boolean },
        unknown
    >;
    forceDeletion?: boolean;
}

export const RemoveAccountConfirmationContent: React.FC<
    RemoveAccountConfirmationContentProps
> = ({ account, back, removeAccountMutation, forceDeletion }) => {
    const [accountName, setAccountName] = useState("");
    const [understood, setUnderstood] = useState(false);

    const {
        lockedTransactionsCount,
        lockedTransactionsLoading,
        hasLockedTransactions,
    } = useLockedTransactionsForFinancialAccount(account);

    return (
        <>
            <StandardModalHeader>
                Please confirm your decision
            </StandardModalHeader>
            <StandardModalBody>
                <p>
                    To remove your account, enter the full name of the account
                    and check the box below.
                </p>
                <AccountCard
                    className={styles.accountName}
                    accountNameConfirmationMode
                    account={account}
                    skipTextBreak
                />
                <Field.Root className={styles.nameField}>
                    <Input
                        disabled={hasLockedTransactions}
                        value={accountName}
                        onChange={(event) => setAccountName(event.target.value)}
                        size="xl"
                    />
                </Field.Root>
                <Checkbox
                    disabled={hasLockedTransactions}
                    label="I understand this will remove the account and all of its historical data. "
                    checked={understood}
                    onCheckedChange={setUnderstood}
                    className={styles.removeCheckbox}
                />
                <p className={styles.warning}>
                    {hasLockedTransactions
                        ? `The account could not be removed because it has ${lockedTransactionsCount} locked ${pluralize("transaction", lockedTransactionsCount)}.`
                        : "Warning: This action cannot be undone."}
                </p>
                <HStack
                    as="footer"
                    gap={12}
                    className={classNames(
                        back
                            ? "justify-content-between"
                            : "justify-content-end",
                    )}
                >
                    {back && (
                        <Button
                            variant="secondary"
                            size="xl"
                            data-testid="confirmation-modal-no-button"
                            onClick={back}
                            disabled={removeAccountMutation.isPending}
                        >
                            Back
                        </Button>
                    )}
                    <ButtonWithLoader
                        disabled={
                            hasLockedTransactions ||
                            lockedTransactionsLoading ||
                            !understood ||
                            !checkIfEnteredAccountNameIsValidForConfirmation(
                                accountName,
                                account,
                            )
                        }
                        onClick={() => {
                            removeAccountMutation.mutate({ forceDeletion });
                        }}
                        loading={removeAccountMutation.isPending}
                        size="xl"
                        data-testid="confirmation-modal-yes-button"
                        variant="destructive"
                    >
                        Remove account
                    </ButtonWithLoader>
                </HStack>
            </StandardModalBody>
        </>
    );
};
